/**
 * Feb 2026 · Hot Deals widget.
 *
 * Surfaces MCube calls in the last N days whose AI post-call brief flagged
 * ``followup_urgency = "high"``. Compact enough for a dashboard card AND
 * standalone enough for the Leads-page top rail. Clicking a row jumps to
 * the lead.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Flame, RefreshCw, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

export default function HotDealsWidget({ compact = false, days = 7, limit = 10, testid = "hot-deals-widget" }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/calls/hot-deals`,
        { params: { days, limit } },
      );
      setRows(data?.rows || []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeWorkspaceId, days, limit]);

  return (
    <div className="border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/40">
        <div className="inline-flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-amber-600" />
          <div className="text-xs font-semibold uppercase tracking-widest">Hot deals</div>
          <span className="text-[10px] text-muted-foreground">last {days}d · urgency=high</span>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="text-muted-foreground hover:text-foreground p-1"
          aria-label="Refresh hot deals"
          data-testid={`${testid}-refresh`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>
      {busy && rows.length === 0 && (
        <div className="p-4 text-xs text-muted-foreground">Loading hot deals…</div>
      )}
      {!busy && rows.length === 0 && (
        <div className="p-4 text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
          No hot deals in the last {days} days. Ask reps to generate AI briefs on their MCube calls to surface urgent follow-ups here.
        </div>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.call_id} className="px-3 py-2 text-xs hover:bg-muted/40" data-testid={`${testid}-row-${r.call_id}`}>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {r.lead_id ? (
                      <Link
                        to={`/app/leads?open=${r.lead_id}`}
                        className="font-semibold hover:underline truncate"
                        data-testid={`${testid}-row-${r.call_id}-lead`}
                      >
                        {r.lead_name || r.customer_number || "—"}
                      </Link>
                    ) : (
                      <span className="font-semibold truncate">{r.lead_name || r.customer_number || "—"}</span>
                    )}
                    {r.ai_deal_stage && (
                      <span className="chip text-[9px] chip-internal">{r.ai_deal_stage}</span>
                    )}
                    {r.ai_sentiment && (
                      <span className={`chip text-[9px] ${
                        r.ai_sentiment === "positive" ? "chip-approved"
                        : r.ai_sentiment === "negative" ? "chip-rejected"
                        : "chip-status"}`}>{r.ai_sentiment}</span>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {fmtWhen(r.analyzed_at || r.created_at)}
                    </span>
                  </div>
                  {r.ai_next_action && (
                    <div className="mt-0.5" data-testid={`${testid}-row-${r.call_id}-next`}>
                      <span className="font-semibold">Next:</span> {r.ai_next_action}
                    </div>
                  )}
                  {!compact && r.ai_key_objection && (
                    <div className="mt-0.5 text-muted-foreground">
                      <span className="font-semibold">Objection:</span> {r.ai_key_objection}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 && (
        <div className="px-3 py-2 border-t border-border bg-muted/20 text-right">
          <Button asChild variant="ghost" size="sm" className="h-7 text-[11px] rounded-none">
            <Link to="/app/leads" data-testid={`${testid}-open-leads`}>
              Open Leads <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
